import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Target, Zap, CheckCircle2, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Phase1ContextPanelProps {
  sessionId: string;
}

interface SessionContext {
  summary?: string;
  primary_tension?: string;
  primary_hypothesis?: string;
  success_criteria?: string[];
  decision_type?: string;
}

export const Phase1ContextPanel = ({ sessionId }: Phase1ContextPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<SessionContext | null>(null);

  useEffect(() => {
    loadContext();
  }, [sessionId]);

  const loadContext = async () => {
    try {
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("summary, decision_type, meta")
        .eq("id", sessionId)
        .single();

      if (session) {
        const meta = (session.meta as Record<string, unknown>) || {};
        setContext({
          summary: session.summary || undefined,
          decision_type: session.decision_type || undefined,
          primary_tension: meta.primary_tension as string | undefined,
          primary_hypothesis: meta.primary_hypothesis as string | undefined,
          success_criteria: meta.success_criteria as string[] | undefined,
        });
      }
    } catch (error) {
      console.error("Error loading context:", error);
    }
  };

  if (!context || (!context.summary && !context.primary_tension && !context.primary_hypothesis)) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-500 hover:border-primary/20">
        {/* Background decorative element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 p-5">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent group">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                  <Brain className="w-4 h-4 text-primary" />
                </div>
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs">Phase 1 Kontext</Badge>
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  Deine Erkenntnisse aus der Analyse
                </span>
              </div>
              <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                )}
              </div>
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="mt-5">
            <div className="grid gap-4 md:grid-cols-3">
              {context.primary_hypothesis && (
                <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-primary/5 to-transparent border border-primary/10 transition-all duration-300 hover:border-primary/20 hover:shadow-[0_10px_30px_-15px_hsl(var(--primary)/0.1)]">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                    <Target className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">
                      Kernhypothese
                    </p>
                    <p className="text-sm text-foreground leading-relaxed">{context.primary_hypothesis}</p>
                  </div>
                </div>
              )}
              
              {context.primary_tension && (
                <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/10 transition-all duration-300 hover:border-amber-500/20 hover:shadow-[0_10px_30px_-15px_hsl(var(--amber-500)/0.1)]">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center shrink-0 group-hover:bg-amber-500 transition-all duration-300">
                    <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1.5">
                      Spannungsfeld
                    </p>
                    <p className="text-sm text-foreground leading-relaxed">{context.primary_tension}</p>
                  </div>
                </div>
              )}
              
              {context.success_criteria && context.success_criteria.length > 0 && (
                <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/10 transition-all duration-300 hover:border-emerald-500/20 hover:shadow-[0_10px_30px_-15px_hsl(var(--emerald-500)/0.1)]">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 transition-all duration-300">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-1.5">
                      Erfolgskriterien
                    </p>
                    <ul className="space-y-1">
                      {context.success_criteria.slice(0, 2).map((c, i) => (
                        <li key={i} className="text-sm text-foreground leading-relaxed">• {c}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  );
};
